import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import "../../styles/layout/MobileAppChrome.css";

type IconName =
  | "home"
  | "grid"
  | "bag"
  | "cart"
  | "wallet"
  | "user"
  | "search"
  | "help"
  | "services"
  | "share"
  | "heart"
  | "bell"
  | "tag";

export const MobileIcon: React.FC<{ name: IconName }> = ({ name }) => {
  const paths: Record<IconName, React.ReactNode> = {
    home: <><path d="M3 11.5 12 4l9 7.5" /><path d="M5.5 10.5V20h13v-9.5M9.5 20v-6h5v6" /></>,
    grid: <><rect x="4" y="4" width="6" height="6" rx="1.5" /><rect x="14" y="4" width="6" height="6" rx="1.5" /><rect x="4" y="14" width="6" height="6" rx="1.5" /><rect x="14" y="14" width="6" height="6" rx="1.5" /></>,
    bag: <><path d="M5 8h14l-1 12H6L5 8Z" /><path d="M9 9V6a3 3 0 0 1 6 0v3" /></>,
    cart: <><path d="M3.5 5h2l1.8 8.2a1 1 0 0 0 1 .8h8.9a1 1 0 0 0 1-.76L20 8H7.2" /><circle cx="10" cy="18.5" r="1.5" /><circle cx="17" cy="18.5" r="1.5" /></>,
    wallet: <><path d="M4 7.5A2.5 2.5 0 0 1 6.5 5H19v14H6.5A2.5 2.5 0 0 1 4 16.5v-9Z" /><path d="M4 8h15M15 12h6v4h-6a2 2 0 0 1 0-4Z" /></>,
    user: <><circle cx="12" cy="8" r="4" /><path d="M4.5 21a7.5 7.5 0 0 1 15 0" /></>,
    search: <><circle cx="11" cy="11" r="6" /><path d="m16 16 4 4" /></>,
    help: <><circle cx="12" cy="12" r="9" /><path d="M9.8 9a2.4 2.4 0 1 1 3.4 2.2c-.8.4-1.2.9-1.2 1.8M12 17h.01" /></>,
    services: <><path d="M7 9a5 5 0 0 1 10 0v2H7V9Z" /><path d="M5 11h14l-1 8H6l-1-8Z" /><path d="M9 5h6" /></>,
    share: <><path d="M12 16V4" /><path d="m7 9 5-5 5 5" /><path d="M6 20h12a2 2 0 0 0 2-2v-2" /></>,
    heart: <><path d="M12 21s-7-4.4-7-10.2C5 7.4 6.9 6 9.1 6c1.4 0 2.5.7 2.9 1.5.4-.8 1.5-1.5 2.9-1.5 2.2 0 4.1 1.4 4.1 4.8C19 16.6 12 21 12 21Z" /></>,
    bell: <><path d="M6 9a6 6 0 0 1 12 0c0 7 2 7 2 9H4c0-2 2-2 2-9" /><path d="M10 21a2 2 0 0 0 4 0" /></>,
    tag: <><path d="M20 12 12 4H5v7l8 8 7-7Z" /><circle cx="8.5" cy="8.5" r="1.2" /></>,
  };

  return <svg className="mobile-app-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
};

export const MobilePageHeader: React.FC<{
  title: string;
  showSearch?: boolean;
  onSearch?: () => void;
  actions?: React.ReactNode;
}> = ({ title, showSearch = false, onSearch, actions }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/");
  };

  return (
    <header className="mobile-page-header">
      {!isHomePage ? (
        <button
          type="button"
          className="mobile-page-header__back"
          onClick={handleBack}
          aria-label="Go back"
        >
          <svg className="mobile-app-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 18 9 12l6-6" />
          </svg>
        </button>
      ) : null}
      <h1>{title}</h1>
      <div className="mobile-page-header__actions">
        {actions}
        {showSearch ? <button type="button" onClick={onSearch} aria-label="Search"><MobileIcon name="search" /></button> : null}
      </div>
    </header>
  );
};

const links: Array<{ to: string; label: string; icon: IconName }> = [
  { to: "/", label: "Home", icon: "home" },
  { to: "/categories", label: "Categories", icon: "grid" },
  { to: "/services", label: "Services", icon: "services" },
  { to: "/wallet", label: "Wallet", icon: "wallet" },
  { to: "/profile", label: "Account", icon: "user" },
];

const MobileBottomNav: React.FC = () => (
  <nav className="mobile-bottom-nav" aria-label="Mobile navigation">
    {links.map((item) => (
      <NavLink key={item.to} to={item.to} end={item.to === "/"} className={({ isActive }) => isActive ? "is-active" : ""}>
        <MobileIcon name={item.icon} />
        <span>{item.label}</span>
      </NavLink>
    ))}
  </nav>
);

export default MobileBottomNav;
